import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { postJob, updateJob } from '../../services/jobsService';
import { resizeServiceImagePreview } from '../../utils/imageUtils';
import '../../css/addjob.css';
import { toast } from 'react-toastify';
import Sortable from "sortablejs";
import { useJobContext } from '../../context/JobContext';
import { US_STATES } from '../../utils/usStates';
import { getImage } from '../../utils/cloudinarySizing';

const ServiceRequestPanel = ({ showAddJob, setShowAddJob, jobToEdit = null, setJobToEdit, setEditedJob }) => {

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budget: '',
    city: '',
    state: '',
    visibility: 'public',
    budget_type: 'flat',
  });
  const visibilityOptions = [
    { value: "public", label: "Public", icon: "fa-solid fa-users" },
    { value: "profile_only", label: "Profile Only", icon: "fa-solid fa-user" },
    { value: "private", label: "Private", icon: "fa-solid fa-lock" },
    { value: "hidden", label: "Hidden", icon: "fa-solid fa-eye-slash" },
  ];

  const [existingImages, setExistingImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [files, setFiles] = useState([]);
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );

  const [brokenImages, setBrokenImages] = useState({});

  const panelRef = useRef(null);

  const { setRequests, notifyJobUpdated } = useJobContext();

  const getVisibilityIcon = (visibility) => {
    const found = visibilityOptions.find((o) => o.value === visibility);
    return found ? found.icon : "";
  };

  const formatVisibility = (visibility) => {
    const found = visibilityOptions.find((o) => o.value === visibility);
    return found ? found.label : "";
  };

  const handleVisibilityChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      visibility: value,
    }));
    setDropdownOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onDrop = (acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      resizeServiceImagePreview(file, (base64) => {
        setPreviews((prev) => [...prev, base64]);
      });
    });

    setFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    },
    multiple: true,
  });

  const removeExistingImage = (id) => {
    setExistingImages(prev => prev.filter(img => img.id !== id));
  };

  const removeNewFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setIsClosing(true);
    setShowAddJob(false);
    setTimeout(() => {
      setIsClosing(false);
      setFormData({
        title: '',
        description: '',
        budget: '',
        location: '',
        visibility: 'public',
        budget_type: 'flat',
      });
      setFiles([]);
      setPreviews([]);
      setJobToEdit(null);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const jobData = new FormData();
      jobData.append('title', formData.title);
      jobData.append('description', formData.description);
      jobData.append('budget', formData.budget || '');
      jobData.append('city', formData.city || '');
      jobData.append('state', formData.state || '');
      jobData.append('visibility', formData.visibility);
      jobData.append('budget_type', formData.budget_type);

      // Append existing image IDs to keep
      existingImages.forEach((img) => {
        jobData.append('existing_images', img.id);  // important: backend should accept this field
      });



      // Append new uploaded files
      files.forEach((file) => {
        jobData.append('images', file);
      });

      if (jobToEdit) {
        const updatedJob = await updateJob(jobToEdit.id, jobData); // 👈 You’ll need to implement this API
        setEditedJob(updatedJob)
        notifyJobUpdated(updatedJob);
        setRequests(prev =>
          prev.map(job => job.id === updatedJob.id ? updatedJob : job)
        );
        toast.success("Job updated successfully!");
      } else {
        const newJob = await postJob(jobData);
        console.log("NEW JOB FROM API:", newJob);
        const jobWithStatus = { ...newJob, status: "active" };

        setRequests(prev => [jobWithStatus, ...prev]);
        toast.success("Job posted successfully!");
      }
      handleClose();
    } catch (error) {
      toast.error("Failed to post job.");
      console.error('Failed to post job:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobToEdit) {
      const formattedBudget = jobToEdit.budget != null
        ? (parseFloat(jobToEdit.budget) % 1 === 0
          ? parseFloat(jobToEdit.budget).toFixed(0)
          : parseFloat(jobToEdit.budget).toFixed(2))
        : ''

      setFormData({
        title: jobToEdit.title || '',
        description: jobToEdit.description || '',
        budget: formattedBudget,
        city: jobToEdit.city || '',
        state: jobToEdit.state || '',
        visibility: jobToEdit.visibility || 'public',
        budget_type: jobToEdit.budget_type || 'flat',
      });
      setExistingImages(jobToEdit.images || []);
      setFiles([]); // for new uploads

    }
  }, [jobToEdit]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    if (!showAddJob) return;

    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowAddJob(false);
        setJobToEdit(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddJob, setShowAddJob, setJobToEdit]);


  if (!showAddJob) return null;


  return (
    <div className="job-slide-in-panel" ref={panelRef} >
      <div className={`job-slide-in-content ${isClosing ? 'fade-out' : 'slide-in'}`}>
        <div className="job-drag-handle">
          <span className="back-button" onClick={handleClose}>&times;</span>
        </div>

        <h2><i className="fa-solid fa-wrench"></i> {jobToEdit ? 'Edit Request' : 'Service Request'}</h2>
        <form onSubmit={handleSubmit}>
          {/* 👇 Dropzone area */}
          {/* Image upload */}
          {!isMobile ? (
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              <p>
                {isDragActive
                  ? 'Drop images here…'
                  : 'Drag & drop images, or click to select'}
              </p>
            </div>
          ) : (
            <div className="mobile-image-upload">
              <input {...getInputProps()} />
              <button
                type="button"
                className="add-image-button"
                onClick={() => document.querySelector('.mobile-image-upload input').click()}
              >
                <i className="fa-solid fa-camera" />
                Add photos
              </button>
            </div>
          )}

          <div className="image-previews">
            {existingImages.map((img) => {
              const isBroken = brokenImages[img.id];

              if (isBroken) return null;

              return (
                <div className="image-thumb-wrapper" key={`existing-${img.id}`}>
                  <img
                    src={getImage(img.image, "avatar_xsm")}
                    alt={img.caption || "existing image"}
                    className="image-thumb"
                    onError={() => {
                      setBrokenImages((prev) => ({
                        ...prev,
                        [img.id]: true,
                      }));
                    }}
                  />

                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => removeExistingImage(img.id)}
                  >
                    ×
                  </button>
                </div>
              );
            })}

            {previews.map((preview, i) => (
              <div className="image-thumb-wrapper" key={`preview-${i}`}>
                <img
                  src={preview}
                  alt={`preview-${i}`}
                  className="image-thumb"
                />
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => removeNewFile(i)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <input
            name="title"
            type="text"
            placeholder="Job Title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
          <textarea
            name="description"
            placeholder="Job Description"
            value={formData.description}
            onChange={handleInputChange}
            required
          />

          <div className="budget-row">
            <div className="input-with-icon">
              <i className="fas fa-dollar-sign input-icon"></i>
              <input
                type="number"
                name="budget"
                placeholder="Amount"
                className="short-field"
                value={formData.budget}
                onChange={handleInputChange}
              />
            </div>

            {/* Budget type select */}
            <div className="select-wrapper select-field">
              <select
                name="budget_type"
                value={formData.budget_type}
                onChange={handleInputChange}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="flat">Flat / Project</option>
                <option value="salary">Salary</option>
              </select>
            </div>
          </div>

          <div className="location-row">
            <div className="input-with-icon">
              <i className="fas fa-city input-icon"></i>
              <input
                type="text"
                name="city"
                placeholder="City / Town"
                value={formData.city}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* State select */}
            <div className="select-wrapper select-field">
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
              >
                <option value="">State</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="job-button-row">
            <button type="submit" disabled={loading}>
              {loading
                ? (jobToEdit ? 'Saving...' : 'Posting...')
                : (jobToEdit ? 'Save Changes' : 'Request')
              }
            </button>
            <button type="button" onClick={handleClose}>
              Close
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ServiceRequestPanel;
